import { useEffect, useRef, useState } from 'react';
import { snapshotUrl } from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Jarak antar penyegaran snapshot.
 *
 * Bukan video: satu kartu = satu JPEG tiap 5 detik, diambil dari frame terakhir
 * worker tile yang memang sudah hidup 24/7 (§D1.1) — tidak ada ffmpeg baru yang
 * dijalankan untuknya. Pada 12 kartu terlihat itu ±2,4 permintaan per detik dan
 * ±45 KB/detik, dua kali lipat lebih murah daripada **satu** tile MJPEG.
 *
 * Halaman Cameras memang halaman inventaris, bukan halaman menonton — yang
 * dicari orang di sini "kamera ini masih mengirim gambar atau tidak", dan
 * pertanyaan itu terjawab oleh gambar yang berumur lima detik.
 */
const REFRESH_MS = 5_000;

interface Props {
  cameraId: string;
  /** Kelas rasio/ukuran dari pemanggil, mis. `aspect-video`. */
  className?: string;
}

/**
 * Thumbnail kamera yang hanya bekerja saat benar-benar terlihat.
 *
 * `IntersectionObserver`, bukan sekadar `loading="lazy"`: yang perlu berhenti
 * bukan cuma pemuatan pertama, tapi **penyegaran berkalanya**. Daftar 96 kamera
 * yang digulung sampai bawah akan meninggalkan 96 timer hidup kalau yang
 * dipakai cuma lazy loading, dan halaman inventaris pelan-pelan berubah jadi
 * beban tetap di server.
 *
 * Tab yang disembunyikan juga berhenti (`visibilitychange`): laptop yang
 * ditinggal dengan tab Cameras terbuka semalaman tidak boleh menarik 17 ribu
 * snapshot sebelum paginya.
 */
export function CameraThumb({ cameraId, className = '' }: Props) {
  const { t } = useBahasa();
  const wadahRef = useRef<HTMLDivElement>(null);
  const [terlihat, setTerlihat] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [gagal, setGagal] = useState(false);

  useEffect(() => {
    const el = wadahRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      ([entry]) => setTerlihat(entry?.isIntersecting ?? false),
      // Margin sedikit lebih tinggi dari nol supaya kartu yang persis di bawah
      // lipatan sudah punya gambarnya saat orang menggulung ke sana.
      { rootMargin: '200px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!terlihat) return;

    const segarkan = () => {
      if (document.visibilityState !== 'visible') return;
      // Kegagalan direset tiap putaran: kamera yang tadi 503 karena baru
      // dinyalakan harus punya kesempatan berikutnya. Tanpa ini satu kegagalan
      // membekukan kartunya sampai halaman dimuat ulang.
      setGagal(false);
      setNonce(Date.now());
    };
    segarkan();

    const t = window.setInterval(segarkan, REFRESH_MS);
    // Kembali dari tab lain langsung menyegarkan, tidak menunggu tick
    // berikutnya — kalau tidak, gambar pertama yang dilihat orang saat kembali
    // adalah gambar dari sebelum ia pergi.
    document.addEventListener('visibilitychange', segarkan);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', segarkan);
    };
  }, [terlihat]);

  return (
    <div
      ref={wadahRef}
      className={`relative overflow-hidden rounded-md bg-subtle ${className}`}
    >
      {terlihat && nonce > 0 && (
        <img
          key={nonce}
          src={snapshotUrl(cameraId, nonce)}
          alt=""
          aria-hidden="true"
          onError={() => setGagal(true)}
          // Disembunyikan, bukan dilepas dari DOM, saat gagal: `<img>` yang
          // src-nya 503 menampilkan ikon rusak bawaan browser, dan itu terbaca
          // sebagai aplikasi yang rusak alih-alih kamera yang belum siap.
          className={`h-full w-full object-cover ${gagal ? 'opacity-0' : ''}`}
        />
      )}

      {/* Kamera yang belum mengirim frame membalas 503 (§C2) — itu keadaan yang
          normal untuk kamera offline, bukan kesalahan yang perlu diteriakkan.
          Teksnya kecil dan diam; status sungguhannya sudah ada di badge kartu. */}
      {(gagal || nonce === 0) && (
        <span className="absolute inset-0 flex items-center justify-center text-xs text-muted">
          {gagal ? t('No image') : ''}
        </span>
      )}
    </div>
  );
}
